import { stateSlotOf } from "./saves";

// What to do about each of a game's save files, given what this browser
// holds, what the cloud holds, and the copy the two last agreed on.

/** The cloud copy this browser last matched: its checksum and the cloud's timestamp for it. */
export interface SyncRecord {
  sum: number;
  at: string;
}

export type SyncRecords = Record<string, SyncRecord>;

export interface LocalFile {
  name: string;
  sum: number;
}

export interface CloudFile {
  name: string;
  updatedAt: string;
}

export interface SyncPlan {
  /** Changed here, not in the cloud: upload. */
  push: string[];
  /** Changed in the cloud, not here: download. */
  pull: string[];
  /** Changed on both sides. */
  conflict: string[];
  /** States removed here whose cloud copy is the one this browser last matched. */
  deleteCloud: string[];
  /** States removed from the cloud whose local copy is the one this browser last matched. */
  deleteLocal: string[];
  /** Records of files gone from both sides. */
  forget: string[];
}

export function checksum(bytes: Uint8Array): number {
  let h = 2166136261;
  for (let i = 0; i < bytes.length; i++) h = Math.imul(h ^ bytes[i], 16777619);
  return h >>> 0;
}

// Removing a state slot is deliberate and propagates; a battery save is
// never deleted by sync on either side.
export function planSync(local: LocalFile[], cloud: CloudFile[], records: SyncRecords): SyncPlan {
  const plan: SyncPlan = { push: [], pull: [], conflict: [], deleteCloud: [], deleteLocal: [], forget: [] };
  const names = new Set([...local.map((f) => f.name), ...cloud.map((f) => f.name), ...Object.keys(records)]);
  for (const name of names) {
    const here = local.find((f) => f.name === name);
    const there = cloud.find((f) => f.name === name);
    const known = records[name];
    const isState = stateSlotOf(name) !== null;
    const localChanged = here ? !known || known.sum !== here.sum : !!known;
    const cloudChanged = there ? !known || known.at !== there.updatedAt : !!known;
    if (!localChanged && !cloudChanged) continue;
    if (!here && !there) plan.forget.push(name);
    else if (here && there) {
      if (localChanged && cloudChanged) plan.conflict.push(name);
      else if (localChanged) plan.push.push(name);
      else plan.pull.push(name);
    } else if (here) {
      // Gone from the cloud: a state the player removed elsewhere, unless
      // it was rewritten here since.
      if (isState && !localChanged) plan.deleteLocal.push(name);
      else plan.push.push(name);
    } else if (isState && !cloudChanged) plan.deleteCloud.push(name);
    else if (cloudChanged) plan.pull.push(name);
  }
  return plan;
}
